import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  CircularProgress
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useSnackbar } from 'notistack';
import { Edit, Delete } from '@mui/icons-material';
import axios from '../../utils/axiosConfig';
import { format } from 'date-fns';

const TrainingManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    startTime: new Date(),
    duration: 60,
    ageGroup: '',
    description: '',
    location: '',
    price: '',
    maxParticipants: 10,
    isPublished: false
  });

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/v2/training/sessions');
      // Sort sessions by createdAt date in descending order (newest first)
      const sortedSessions = response.data.sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      setSessions(sortedSessions);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('userRole');
        delete axios.defaults.headers.common['Authorization'];
        navigate('/admin', { state: { from: location.pathname } });
      } else {
        enqueueSnackbar('Error fetching sessions', { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDateChange = (newValue) => {
    setFormData(prev => ({
      ...prev,
      startTime: newValue
    }));
  };

  const handlePublishToggle = async (sessionId, currentStatus) => {
    try {
      await axios.patch(`/api/v2/training/sessions/${sessionId}/publish`, {
        isPublished: !currentStatus
      });
      await fetchSessions();
      enqueueSnackbar('Session status updated', { variant: 'success' });
    } catch (error) {
      console.error('Error updating session status:', error);
      enqueueSnackbar(error.response?.data?.message || 'Error updating session status', { variant: 'error' });
    }
  };

  const handleEdit = (session) => {
    setFormData({
      title: session.title,
      startTime: new Date(session.startTime),
      duration: session.duration,
      ageGroup: session.ageGroup,
      description: session.description,
      location: session.location,
      price: session.price,
      maxParticipants: session.maxParticipants,
      isPublished: session.isPublished
    });
    setEditingSession(session);
    setOpenDialog(true);
  };

  const handleDelete = async (sessionId) => {
    if (window.confirm('Are you sure you want to delete this session?')) {
      try {
        await axios.delete(`/api/v2/training/sessions/${sessionId}`);
        enqueueSnackbar('Session deleted successfully', { variant: 'success' });
        fetchSessions();
      } catch (error) {
        console.error('Error deleting session:', error);
        enqueueSnackbar('Failed to delete session', { variant: 'error' });
      }
    }
  };

  const validateForm = () => {
    if (!formData.title.trim()) return 'Title is required';
    if (!formData.startTime) return 'Start time is required';
    if (!formData.duration || formData.duration <= 0) return 'Duration must be a positive number';
    if (!formData.ageGroup) return 'Age group is required';
    if (!formData.description.trim()) return 'Description is required';
    if (!formData.location.trim()) return 'Location is required';
    if (!formData.price || formData.price < 0) return 'Price must be a non-negative number';
    if (!formData.maxParticipants || formData.maxParticipants <= 0) return 'Max participants must be a positive number';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const error = validateForm();
    if (error) {
      enqueueSnackbar(error, { variant: 'error' });
      return;
    }

    setLoading(true);
    
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        maxParticipants: parseInt(formData.maxParticipants),
        duration: parseInt(formData.duration),
        startTime: formData.startTime.toISOString()
      };

      if (editingSession) {
        await axios.put(`/api/v2/training/sessions/${editingSession._id}`, payload);
        enqueueSnackbar('Session updated successfully', { variant: 'success' });
      } else {
        await axios.post('/api/v2/training/sessions', payload);
        enqueueSnackbar('Session created successfully', { variant: 'success' });
      }
      
      setOpenDialog(false);
      setEditingSession(null);
      setFormData({
        title: '',
        startTime: new Date(),
        duration: 60,
        ageGroup: '',
        description: '',
        location: '',
        price: '',
        maxParticipants: 10,
        isPublished: false
      });
      fetchSessions();
    } catch (error) {
      console.error('Error saving session:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('userRole');
        delete axios.defaults.headers.common['Authorization'];
        navigate('/admin', { state: { from: location.pathname } });
      } else {
        enqueueSnackbar('Failed to save session', { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  const renderSessionForm = () => (
    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            error={!formData.title.trim()}
            helperText={!formData.title.trim() ? 'Title is required' : ''}
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              label="Start Time"
              value={formData.startTime}
              onChange={handleDateChange}
              slotProps={{ 
                textField: { 
                  fullWidth: true, 
                  required: true,
                  error: !formData.startTime,
                  helperText: !formData.startTime ? 'Start time is required' : ''
                } 
              }}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Duration (minutes)"
            name="duration"
            type="number"
            value={formData.duration}
            onChange={handleChange}
            required
            error={!formData.duration || formData.duration <= 0}
            helperText={!formData.duration || formData.duration <= 0 ? 'Duration must be a positive number' : ''}
            InputProps={{ inputProps: { min: 1 } }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth required error={!formData.ageGroup}>
            <InputLabel>Age Group</InputLabel>
            <Select
              name="ageGroup"
              value={formData.ageGroup}
              onChange={handleChange}
              label="Age Group"
            >
              <MenuItem value="5th/6th Grade">5th/6th Grade</MenuItem>
              <MenuItem value="7th/8th Grade">7th/8th Grade</MenuItem>
              <MenuItem value="High School">High School</MenuItem>
              <MenuItem value="Adult">Adult</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Location"
            name="location"
            value={formData.location}
            onChange={handleChange}
            required
            error={!formData.location.trim()}
            helperText={!formData.location.trim() ? 'Location is required' : ''}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Price"
            name="price"
            type="number"
            value={formData.price}
            onChange={handleChange}
            required
            error={!formData.price || formData.price < 0}
            helperText={!formData.price || formData.price < 0 ? 'Price must be a non-negative number' : ''}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              inputProps: { min: 0, step: 0.01 }
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Max Participants"
            name="maxParticipants"
            type="number"
            value={formData.maxParticipants}
            onChange={handleChange}
            required
            error={!formData.maxParticipants || formData.maxParticipants <= 0}
            helperText={!formData.maxParticipants || formData.maxParticipants <= 0 ? 'Max participants must be a positive number' : ''}
            InputProps={{ inputProps: { min: 1 } }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Description"
            name="description"
            multiline
            rows={4}
            value={formData.description}
            onChange={handleChange}
            required
            error={!formData.description.trim()}
            helperText={!formData.description.trim() ? 'Description is required' : ''}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.isPublished}
                onChange={(e) => handleChange({ target: { name: 'isPublished', value: e.target.checked }})}
                name="isPublished"
              />
            }
            label="Publish Session"
          />
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Training Sessions</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setEditingSession(null);
            setFormData({
              title: '',
              startTime: new Date(),
              duration: 60,
              ageGroup: '',
              description: '',
              location: '',
              price: '',
              maxParticipants: 10,
              isPublished: false
            });
            setOpenDialog(true);
          }}
        >
          Create New Session
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <CircularProgress />
        </Box>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Start Time</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Age Group</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sessions.map((session) => (
              <TableRow key={session._id}>
                <TableCell>{session.title}</TableCell>
                <TableCell>{format(new Date(session.startTime), 'PPp')}</TableCell>
                <TableCell>{session.duration} mins</TableCell>
                <TableCell>{session.ageGroup}</TableCell>
                <TableCell>{session.location}</TableCell>
                <TableCell>${session.price}</TableCell>
                <TableCell>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={session.isPublished}
                        onChange={() => handlePublishToggle(session._id, session.isPublished)}
                      />
                    }
                    label={session.isPublished ? "Published" : "Draft"}
                  />
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEdit(session)} color="primary">
                    <Edit />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(session._id)} color="error">
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingSession ? 'Edit Training Session' : 'Create Training Session'}
        </DialogTitle>
        <DialogContent>
          {renderSessionForm()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleSubmit}
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : (editingSession ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrainingManagement;
